import { useMemo } from "react";
import { Link } from "react-router";
import ReactMarkdown from "react-markdown";
import { useLocale } from "../locale/locale-context.tsx";

// Markdown bodies are bundled as raw strings via Vite's `?raw` query so the
// content ships with the SPA — no runtime fetch, no extra round trip, and
// translations swap instantly when the user toggles locale.
import termsEn from "../content/legal/en/terms.md?raw";
import privacyEn from "../content/legal/en/privacy.md?raw";
import cookiesEn from "../content/legal/en/cookies.md?raw";
import refundEn from "../content/legal/en/refund-policy.md?raw";
import termsTh from "../content/legal/th/terms.md?raw";
import privacyTh from "../content/legal/th/privacy.md?raw";
import cookiesTh from "../content/legal/th/cookies.md?raw";
import refundTh from "../content/legal/th/refund-policy.md?raw";

export type LegalSlug = "terms" | "privacy" | "cookies" | "refund-policy";

const CONTENT: Record<LegalSlug, { en: string; th: string }> = {
  terms: { en: termsEn, th: termsTh },
  privacy: { en: privacyEn, th: privacyTh },
  cookies: { en: cookiesEn, th: cookiesTh },
  "refund-policy": { en: refundEn, th: refundTh },
};

// The four legal pages share one rendered shell; the title is shown as a
// styled hero (the markdown's H1 is hidden). Title keys live in
// `packages/i18n/src/catalog.ts` under the `legal.*.title` namespace.
const TITLE_KEYS: Record<LegalSlug, string> = {
  terms: "legal.terms.title",
  privacy: "legal.privacy.title",
  cookies: "legal.cookies.title",
  "refund-policy": "legal.refund.title",
};

// Pull the "Last updated: …" line out of the markdown so we can render it as
// a styled footer chip rather than as inline italic text inside the body.
function extractLastUpdated(md: string): { body: string; lastUpdated: string | null } {
  // Match the italic line that always follows the H1 in our templates.
  const match = md.match(/^_(.+?)_\s*$/m);
  if (!match) return { body: md, lastUpdated: null };
  const lastUpdated = match[1] ?? null;
  // Strip both the H1 and the italic line; we re-render the title ourselves.
  const body = md
    .replace(/^#\s.+\n/, "")
    .replace(/^_.+?_\s*\n/m, "")
    .trim();
  return { body, lastUpdated };
}

interface LegalPageProps {
  slug: LegalSlug;
}

export function LegalPage({ slug }: LegalPageProps) {
  const { locale, t } = useLocale();
  const isDev = import.meta.env.DEV;

  const title = t(TITLE_KEYS[slug]);
  const { body, lastUpdated } = useMemo(() => {
    const raw = CONTENT[slug][locale];
    const { body: stripped, lastUpdated: lu } = extractLastUpdated(raw);
    return { body: stripped, lastUpdated: lu };
  }, [slug, locale]);

  return (
    <div className="relative min-h-[calc(100vh-4rem)] overflow-hidden bg-gradient-to-br from-[#fff8fa] via-[#fce6ee]/60 to-[var(--brand-paper)]">
      {/* Soft corner blooms mirror the admin login + error pages so the entire
          customer-side experience reads as one consistent surface. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 [background:radial-gradient(45rem_30rem_at_15%_5%,rgba(247,200,213,0.45),transparent_60%),radial-gradient(55rem_40rem_at_95%_100%,rgba(233,75,122,0.10),transparent_65%)]"
      />

      <article className="relative mx-auto max-w-3xl px-6 py-16 sm:px-8 sm:py-20">
        <header className="mb-10 border-b border-[var(--brand-border)] pb-8">
          {isDev ? (
            <span className="mb-4 inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-medium uppercase tracking-wide text-amber-900 ring-1 ring-inset ring-amber-200">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500" aria-hidden />
              Draft — pending lawyer review
            </span>
          ) : null}
          <h1 className="font-heading text-3xl font-bold leading-tight tracking-tight text-[var(--brand-ink)] sm:text-4xl">
            {title}
          </h1>
          {lastUpdated ? (
            <p className="mt-3 text-sm text-[var(--brand-muted)]">{lastUpdated}</p>
          ) : null}
        </header>

        <div className="legal-prose text-[15px] leading-7 text-[var(--brand-ink)]/85">
          <ReactMarkdown
            components={{
              h1: ({ children }) => (
                <h1 className="mt-12 font-heading text-2xl font-bold text-[var(--brand-ink)]">
                  {children}
                </h1>
              ),
              h2: ({ children }) => (
                <h2 className="mt-10 mb-3 font-heading text-xl font-bold tracking-tight text-[var(--brand-ink)]">
                  {children}
                </h2>
              ),
              h3: ({ children }) => (
                <h3 className="mt-6 mb-2 font-heading text-base font-semibold text-[var(--brand-ink)]">
                  {children}
                </h3>
              ),
              p: ({ children }) => <p className="mb-4">{children}</p>,
              a: ({ href, children }) => {
                const isInternal = href?.startsWith("/");
                if (isInternal) {
                  return (
                    <Link
                      to={href ?? "#"}
                      className="font-medium text-[var(--brand-rose)] underline decoration-[var(--brand-rose)]/30 underline-offset-4 transition-colors hover:decoration-[var(--brand-rose)]"
                    >
                      {children}
                    </Link>
                  );
                }
                return (
                  <a
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium text-[var(--brand-rose)] underline decoration-[var(--brand-rose)]/30 underline-offset-4 transition-colors hover:decoration-[var(--brand-rose)]"
                  >
                    {children}
                  </a>
                );
              },
              strong: ({ children }) => (
                <strong className="font-semibold text-[var(--brand-ink)]">{children}</strong>
              ),
              em: ({ children }) => <em className="text-[var(--brand-muted)]">{children}</em>,
              ul: ({ children }) => <ul className="mb-4 ml-6 list-disc space-y-1.5">{children}</ul>,
              ol: ({ children }) => (
                <ol className="mb-4 ml-6 list-decimal space-y-1.5">{children}</ol>
              ),
              li: ({ children }) => <li className="pl-1">{children}</li>,
              code: ({ children }) => (
                <code className="rounded bg-[var(--brand-pink-soft)] px-1.5 py-0.5 font-mono text-[13px] text-[var(--brand-ink)]">
                  {children}
                </code>
              ),
            }}
          >
            {body}
          </ReactMarkdown>
        </div>

        <footer className="mt-16 border-t border-[var(--brand-border)] pt-8 text-sm text-[var(--brand-muted)]">
          <p>
            {t("legal.contact_prefix")}
            <a
              href="mailto:legal@bloom.tv"
              className="font-medium text-[var(--brand-rose)] hover:underline"
            >
              legal@bloom.tv
            </a>
          </p>
        </footer>
      </article>
    </div>
  );
}
